import React, { useState, useEffect } from 'react';
import { convertCurrency } from '../services/currencyService';

interface AccountValueProps {
  value: number;
  currency: string;
  userPreferredCurrency: string;
  showOriginal?: boolean;
}

const AccountValue: React.FC<AccountValueProps> = ({ 
  value, 
  currency, 
  userPreferredCurrency, 
  showOriginal = true 
}) => {
  const [convertedValue, setConvertedValue] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let isMounted = true;
    
    const convertValue = async () => {
      if (currency === userPreferredCurrency) {
        if (isMounted) {
          setConvertedValue(null);
        }
        return;
      }

      if (isMounted) {
        setLoading(true);
      }
      
      try {
        const converted = await convertCurrency(value, currency, userPreferredCurrency);
        if (isMounted) {
          setConvertedValue(converted);
        }
      } catch (error) {
        console.error('Error converting currency:', error);
        if (isMounted) {
          setConvertedValue(null);
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    convertValue();

    // Cleanup function to prevent memory leaks
    return () => {
      isMounted = false;
    };
  }, [value, currency, userPreferredCurrency]);

  const formatValue = (val: number, curr: string) => {
    // Handle NaN, null, undefined values
    if (val === null || val === undefined || isNaN(val)) {
      return new Intl.NumberFormat('en-CA', {
        style: 'currency',
        currency: curr,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(0);
    }
    
    return new Intl.NumberFormat('en-CA', {
      style: 'currency',
      currency: curr,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(val);
  };

  if (currency === userPreferredCurrency) {
    return <span>{formatValue(value, currency)}</span>;
  }

  if (loading) {
    return <span className="text-secondary">Converting...</span>;
  }

  if (convertedValue === null) {
    return <span>{formatValue(value, currency)}</span>;
  }

  return (
    <div className="account-value-display">
      <div className="converted-value">
        {formatValue(convertedValue, userPreferredCurrency)}
      </div>
      {showOriginal && (
        <div className="original-value">
          {formatValue(value, currency)}
        </div>
      )}
    </div>
  );
};

export default AccountValue; 